import { FC, useEffect, useState, useCallback } from 'react';
import { request } from 'src/common/request';
import { useCurrentSpace } from 'src/hooks/space';
import {
  IntegrationItem,
  IconData,
  BotDTO,
  IntegrationDetailResponse,
} from '@next-space/fe-api-idl';
import { Button } from '@flowus/common/components/button';
import { useOpenModal } from '@flowus/common/next-modal';
import { message } from '@flowus/common/components/message';
import { Avatar } from 'src/common/components/avatar';
import { IntegrationType } from 'src/views/dev/integrations/types';
import { LoadingIcon } from 'src/common/components/loading-icon';
import { Empty } from 'src/views/main/template/empty';
import dayjs from 'dayjs';
import { SelectSpacePage } from 'src/views/oauth/select-space-page';
import { useTocIds } from '../../aside/toc/use-toc-id';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { getCurrentUser } from 'src/hooks/user';
import { SelectPageSetting } from './select-page-setting';
import { ViewPath } from 'src/utils';

export const Integrations: FC = () => {
  const currentSpace = useCurrentSpace();
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);

  const openModal = useOpenModal();

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.integration.getSpaceIntegrations(currentSpace.uuid);
      const items: IntegrationItem[] = res.integrations ?? [];
      setIntegrations(items ?? []);
    } catch (e) {
      message.error('获取应用列表失败');
    } finally {
      setLoading(false);
    }
  }, [currentSpace.uuid]);

  useEffect(() => {
    void fetchIntegrations();
  }, [fetchIntegrations]);

  const handleRevoke = (integration: IntegrationItem) => {
    openModal.warning({
      title: '取消授权确认',
      content: (
        <div className="space-y-3">
          <p>
            <span>确定要取消对 </span>
            <span>"{integration.name}"</span>
            <span>的授权吗？</span>
          </p>
          <div className="text-t4 text-grey3 space-y-1">
            <p>• 应用将无法访问您的工作空间数据</p>
            <p>• 所有相关的自动化功能将停止工作</p>
            <p>• 如需重新使用，需要重新进行授权</p>
          </div>
          <p className="text-t4 text-grey3">此操作不可撤销，请谨慎操作。</p>
        </div>
      ),
      confirm: async () => {
        try {
          const botId = integration.bots?.[0]?.uuid;
          if (botId) {
            if (integration.type === 'internal') {
              //如果是内部应用就没办法删掉机器人了
              await request.integration.updateBotPages(botId, {
                blockIdsToAdd: [],
              });
            } else if (integration.type === 'external') {
              //如果是外部应用，则直接删掉机器人
              await request.integration.deleteBot(botId);
            }
            message.success('已成功取消授权');
          }
          void fetchIntegrations(); // Refresh list
        } catch (e) {
          message.error('取消授权失败');
        }
      },
      confirmText: '确认取消',
      cancelText: '保留授权',
      colorType: 'red',
    });
  };

  const handleSettings = async (e: React.MouseEvent, integration: IntegrationItem) => {
    const bot = integration.bots?.[0];
    if (!bot) {
      message.error('找不到对应的授权信息，无法设置');
      return;
    }
    openModal.modal({
      content: ({ onCloseModal }) => {
        return (
          <SelectPageSetting bot={bot} integration={integration} onCloseModal={onCloseModal} />
        );
      },
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return dayjs(date).format('YYYY-MM-DD');
  };

  if (loading) {
    return (
      <div className="h-40 flex items-center justify-center">
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="flex justify-between items-center mb-6 px-6">
        <h2 className="text-grey3 text-t2">已授权应用</h2>
        <a className="underline" href={ViewPath.integrations} target="_blank">
          开发者中心
        </a>
      </div>

      {integrations.length > 0 ? (
        <div className="space-y-4 px-6">
          {integrations.map((integration) => {
            const isIconData = integration.icon && typeof integration.icon === 'object';
            const iconData = isIconData ? (integration.icon as IconData) : undefined;
            const iconUrl = typeof integration.icon === 'string' ? integration.icon : '';

            return (
              <div
                key={integration.id}
                className="border border-grey6 rounded-lg p-2 hover:border-grey4 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center flex-1">
                    {iconData ? (
                      <Avatar
                        name={integration.name || 'App'}
                        icon={iconData}
                        iconSize={40}
                        className="w-10 h-10 mr-4 rounded-md"
                      />
                    ) : iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={`${integration.name || 'App'} Icon`}
                        className="w-10 h-10 rounded-md mr-4 object-contain bg-grey7"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-grey7 flex items-center justify-center mr-4">
                        <span className="text-grey3 text-t1 font-medium">
                          {(integration.name || 'A').substring(0, 1).toUpperCase()}
                        </span>
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h3 className="text-t1 font-medium mr-3">
                          {integration.name || 'Unnamed App'}
                        </h3>
                        <span className="text-t4 text-grey3 bg-grey7 px-2 py-0.5 rounded">
                          {integration.type === IntegrationType.INTERNAL ? '应用插件' : '外部应用'}
                        </span>
                      </div>

                      <div className="text-t3 text-grey3 mb-2">
                        {formatDate(integration.createdAt)} 授权
                      </div>

                      {integration.description && (
                        <p className="text-t3 text-grey2 mb-3 line-clamp-2">
                          {integration.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button onClick={(e) => handleSettings(e, integration)}>页面访问权限</Button>
                    <Button onClick={() => handleRevoke(integration)}>取消授权</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-40 flex flex-col items-center justify-center">
          <p className="text-t3 text-grey3 mt-4 mb-6">暂无已授权应用</p>
        </div>
      )}
    </div>
  );
};
